import { Box, Button, Chip, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponseByIdQuery } from '../../hooks/queries/qhse/forms/useFormResponsesQuery';
import { FormTemplateWithSections } from '../../services/apiSLTP/qhse/forms/formTemplates';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import Icon from '../shared/Icon';
import IconButton from '../shared/IconButton';
import SectionItem from './sections/SectionItem';
import SectionResponseItem from './responses/SectionResponseItem';
import { useFormResponseDocumentsQuery } from '../../hooks/queries/qhse/forms/useFormResponseDocumentsQuery';
import { useFormAttendedUsersQuery } from '../../hooks/queries/qhse/forms/useFormResponseUsersQuery';
import { getContractTypeLabel } from '../../constants/labels/user';
import FormResponsePDF from '../pdf/FormResponsePDF';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useBoolean } from '../../hooks/useBoolean';
import PDFModal from '../modals/PDFModal';

interface FormResponseProps {
    formTemplate: FormTemplateWithSections;
    selectedResponseId: string;
    onBack: () => void;
}

const FormResponse: React.FC<FormResponseProps> = ({ formTemplate, selectedResponseId, onBack }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const pdfViewerRef = useRef<HTMLIFrameElement>(null);
    const open = Boolean(anchorEl);
    const pdfViewerModal = useBoolean(false);

    const { data: dataResponse, isLoading: isResponseLoading, refetch: refetchResponse } = useResponseByIdQuery({ id: selectedResponseId });
    const {
        data: dataDocuments,
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments,
    } = useFormResponseDocumentsQuery({ responseId: selectedResponseId });
    const {
        data: dataAttendedUsers,
        isLoading: isLoadingAttendedUsers,
        refetch: refetchAttendedUsers,
    } = useFormAttendedUsersQuery({ responseId: selectedResponseId });

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handlePrintPDF = async () => {
        handleMenuClose();

        if (!dataResponse) return;

        try {
            const blob = await pdf(
                <FormResponsePDF
                    formTemplate={formTemplate}
                    formResponse={dataResponse}
                    documents={dataDocuments?.data}
                    attendees={dataAttendedUsers?.data}
                    t={t}
                />
            ).toBlob();

            /*const url = URL.createObjectURL(blob);
            window.open(url, '_blank');*/

            /*const fileName = `${formTemplate.title}_${formatName(dataResponse.filledBy.firstName, dataResponse.filledBy.lastName)}_${new Date(dataResponse.submittedAt || dataResponse.createdAt).toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName; // Suggestion de nom sur desktop
            link.target = '_blank'; // Important pour mobile
            link.click();

            setTimeout(() => URL.revokeObjectURL(url), 3000);*/

            const url = URL.createObjectURL(blob);
            const printFrame = document.createElement('iframe');

            printFrame.style.position = 'fixed';
            printFrame.style.right = '0';
            printFrame.style.bottom = '0';
            printFrame.style.width = '0';
            printFrame.style.height = '0';
            printFrame.style.border = 'none';
            printFrame.style.visibility = 'hidden';

            const fileName = `${formTemplate.title}_${formatName(dataResponse.filledBy.firstName, dataResponse.filledBy.lastName)}_${new Date(dataResponse.submittedAt || dataResponse.createdAt).toLocaleDateString('fr-FR').replace(/\//g, '-')}`;

            document.body.appendChild(printFrame);
            printFrame.src = url;

            printFrame.onload = () => {
                setTimeout(() => {
                    try {
                        if (printFrame.contentDocument) {
                            printFrame.contentDocument.title = fileName;
                        }
                    } catch (e) {
                        console.warn('Impossible de définir le titre:', e);
                    }

                    const printWindow = printFrame.contentWindow;
                    if (!printWindow) return;

                    const cleanup = () => {
                        document.body.removeChild(printFrame);
                        URL.revokeObjectURL(url);
                        printWindow.removeEventListener('afterprint', cleanup);
                    };

                    printWindow.addEventListener('afterprint', cleanup);
                    printWindow.focus();
                    printWindow.print();
                }, 250);
            };

            // Gestion des erreurs de chargement
            printFrame.onerror = () => {
                console.error("Erreur lors du chargement du PDF dans l'iframe");
                document.body.removeChild(printFrame);
                URL.revokeObjectURL(url);
            };
        } catch (error) {
            console.error("Erreur lors de l'impression du PDF:", error);
        }
    };

    return (
        <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row', gap: 2 }}>
                    <IconButton iconName="arrowBack" onClick={onBack} />
                    <Typography variant="h4">{formatName(dataResponse?.filledBy.firstName, dataResponse?.filledBy.lastName)}</Typography>
                    <Typography variant="h6">{dataResponse?.submittedAt ? new Date(dataResponse?.submittedAt).toLocaleString() : ''}</Typography>
                </Box>
                <Box>
                    <IconButton iconName="print" onClick={pdfViewerModal.setTrue} />
                </Box>
            </Box>

            {formTemplate.isSiteEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 2 }}>
                    <Typography variant="subtitle1">{dataResponse?.siteId?.name}</Typography>
                </Box>
            )}

            {(dataResponse?.attendees.length || 0) > 0 && (
                <Stack sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                    {dataResponse?.attendees.map((attendee) => {
                        const isValidated = attendee.hasValidatedPresence;
                        const validationDate = attendee.validatedAt ? new Date(attendee.validatedAt).toLocaleString('fr-FR') : null;
                        const contractTypeLabel = getContractTypeLabel(attendee.contractType).toUpperCase();

                        return (
                            <Box
                                key={attendee._id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                }}
                            >
                                <Chip
                                    label={`${attendee.name || formatName(attendee.userId?.firstName, attendee.userId?.lastName)} (${contractTypeLabel})${validationDate ? ` - ${validationDate}` : ''}`}
                                    color={attendee.name ? 'default' : isValidated ? 'success' : 'warning'}
                                    size="small"
                                    icon={<Icon name={attendee.name ? 'personOutline' : 'person'} />}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {(dataDocuments?.data.length || 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {dataDocuments?.data.map(
                        (doc) =>
                            doc.file?.url && (
                                <Box key={doc._id} sx={{ width: 120, height: 120, position: 'relative', cursor: 'pointer' }}>
                                    <img
                                        src={doc.file.url}
                                        alt={doc.file.fileName || 'photo'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                                        onClick={() => window.open(doc.file?.url, '_blank')}
                                    />
                                </Box>
                            )
                    )}
                </Box>
            )}

            <Box sx={{}}>
                {dataResponse &&
                    formTemplate.sections.map((section, sectionIndex) => (
                        <SectionResponseItem
                            key={section._id}
                            section={section}
                            sectionIndex={sectionIndex}
                            formColor={formTemplate.type?.color || '#000'}
                            response={dataResponse}
                        />
                    ))}
            </Box>
            {pdfViewerModal.value && dataResponse && (
                <PDFModal
                    open={pdfViewerModal.value}
                    handleClose={pdfViewerModal.setFalse}
                    fileName={`${formTemplate.title}_${formatName(dataResponse.filledBy.firstName, dataResponse.filledBy.lastName)}_${new Date(dataResponse.submittedAt || dataResponse.createdAt).toLocaleDateString('fr-FR').replace(/\//g, '-')}`}
                    PDFComponent={() => (
                        <FormResponsePDF
                            formTemplate={formTemplate}
                            formResponse={dataResponse}
                            documents={dataDocuments?.data}
                            attendees={dataAttendedUsers?.data}
                            t={t}
                        />
                    )}
                />
            )}
        </Box>
    );
};

export default FormResponse;
